import { z, ZodTypeAny } from 'zod'
import { DocumentType, PaymentMethod } from './orders'
import { Category, PillowType, TableType, Unit } from './products'
import { ShortageStatus } from './shortage'
import { OrderPlace, WorkType } from './work'

export const zodInputStringPipe = (zodPipe: ZodTypeAny) =>
  z
    .string()
    .transform((value) => {
      return value === '' ? null : value
    })
    .nullable()
    .refine(
      (value) => {
        if (value === null || isNaN(Number(value))) {
          return false
        }
        return true
      },
      {
        message: 'Nieprawidłowa liczba',
      }
    )
    .transform((value) => (value === null ? 0 : Number(value)))
    .pipe(zodPipe)

export const placeSchema = z.object({
  rack: z
    .number({ required_error: 'Podaj regał', invalid_type_error: 'Numer regału musi być liczbą' })
    .positive({ message: 'Podaj poprawny numer regału' })
    .int({ message: 'Podaj poprawny numer regału' })
    .max(99, { message: 'Numer regału musi być mniejszy od 100' }),
  column: z
    .number({ required_error: 'Podaj kolumnę', invalid_type_error: 'Numer kolumny musi być liczbą' })
    .positive({ message: 'Podaj poprawny numer kolumny' })
    .int({ message: 'Podaj poprawny numer kolumny' })
    .max(99, { message: 'Numer kolumny musi być mniejszy od 100' }),
  shelf: z
    .number({ required_error: 'Podaj półkę', invalid_type_error: 'Numer półki musi być liczbą' })
    .positive({ message: 'Podaj poprawny numer półki' })
    .int({ message: 'Podaj poprawny numer półki' })
    .max(99, { message: 'Numer półki musi być mniejszy od 100' }),
})

export const variantSchema = z.object({
  stock: z.number({ required_error: 'Podaj ilość' }).positive({ message: 'Ilość musi być większa od zera' }),
  unit: z.nativeEnum(Unit),
  place: placeSchema,
  comments: z.string().optional().nullable(),
})

export const fabricVariantUniqueProperty = z.object({
  size: z.number({ required_error: 'Podaj wysokość' }),
  feature: z.string().optional().nullable(),
})

export const premadeVariantUniqueProperty = z.object({
  height: z.number({ required_error: 'Podaj wysokość' }),
  width: z.number({ required_error: 'Podaj szerokość' }),
  finish: z.array(z.string()),
})

export const pillowVariantUniqueProperty = z.object({
  size: z
    .string({ required_error: 'Podaj wymiar' })
    .regex(/^(?:[1-9]\d)x(?:[1-9]\d)$/, { message: 'Podaj poprawny wymiar, np. 40x40' }),
  pillowType: z.nativeEnum(PillowType),
  feature: z.string().optional().nullable(),
})

export const tableVariantUniqueProperty = z.object({
  length: z.number({ required_error: 'Podaj długość' }),
  width: z.number({ required_error: 'Podaj szerokość' }),
  tableType: z.nativeEnum(TableType),
  feature: z.string().optional().nullable(),
})

export const towelVariantUniqueProperty = z.object({
  length: z.number({ required_error: 'Podaj długość' }),
  width: z.number({ required_error: 'Podaj szerokość' }),
  feature: z.string().optional().nullable(),
})

export const fabricVariantSchema = variantSchema.merge(fabricVariantUniqueProperty)
export const premadeVariantSchema = variantSchema.merge(premadeVariantUniqueProperty)
export const pillowVariantSchema = variantSchema.merge(pillowVariantUniqueProperty)
export const tableVariantSchema = variantSchema.merge(tableVariantUniqueProperty)
export const towelVariantSchema = variantSchema.merge(towelVariantUniqueProperty)

export const productSchema = z.object({
  symbol: z
    .string({ required_error: 'Podaj symbol nowego produktu lub wybierz istniejący produkt' })
    .min(1, { message: 'Podaj symbol nowego produktu lub wybierz istniejący produkt' }),
  alternateSymbol: z.string().optional().nullable(),
  image: z
    .union([
      z.literal(''),
      z.string().url({ message: 'Link musi być poprawnym adresem URL' }).startsWith('https://abcfirany.pl', {
        message: 'Link musi prowadzić do zdjęcia z abcfirany.pl',
      }),
    ])
    .optional()
    .nullable(),
  isAtSale: z.boolean(),
  manufacturerId: z.string({ required_error: 'Wybierz producenta', invalid_type_error: 'Wybierz producenta' }),
  availableSizes: z.array(z.string()).optional().nullable(),
})

export const newProductSchema = variantSchema.merge(productSchema)

export const newFabricProductSchema = newProductSchema.merge(fabricVariantUniqueProperty)
export const newPremadeProductSchema = newProductSchema.merge(premadeVariantUniqueProperty)
export const newPillowProductSchema = newProductSchema.merge(pillowVariantUniqueProperty)
export const newTableProductSchema = newProductSchema.merge(tableVariantUniqueProperty)
export const newTowelProductSchema = newProductSchema.merge(towelVariantUniqueProperty)

export const manufacturerSchema = z.object({
  name: z.string({ required_error: 'Podaj nazwę producenta' }),
  email: z.string({ required_error: 'Podaj adres email' }).email({ message: 'Podaj poprawny adres email' }),
})

export const workSchema = z.object({
  orderNumber: z.string({ required_error: 'Podaj numer zamówienia' }),
  amount: z
    .number({ required_error: 'Podaj ilość wykonanej pracy' })
    .positive({ message: 'Ilość wykonanej pracy musi być większa od zera' }),
  place: z.nativeEnum(OrderPlace, { required_error: 'Wybierz miejsce' }),
  workType: z.nativeEnum(WorkType, { required_error: 'Wybierz rodzaj pracy' }),
  comments: z.string().optional().nullable(),
})

export const shortageSchema = z.object({
  productId: z.string({ required_error: 'Wybierz produkt', invalid_type_error: 'Wybierz produkt' }),
  category: z.nativeEnum(Category, {
    required_error: 'Wybierz rodzaj produktu',
    invalid_type_error: 'Wybierz rodzaj produktu',
  }),
  size: z.string({ required_error: 'Podaj wymiar' }).regex(/^(?:\d+(?:x\d+)?)(?:,\d+(?:x\d+)?)*$/, {
    message: 'Podaj poprawny wymiar, np. 250, 140x200 lub 140x200,160x200',
  }),
  feature: z.string().optional().nullable(),
  amount: z
    .number({ required_error: 'Podaj ilość brakującego produktu' })
    .positive({ message: 'Ilość brakującego produktu musi być większa od zera' }),
  unit: z.nativeEnum(Unit, { required_error: 'Wybierz jednostkę' }),
  orderNumber: z.string({ required_error: 'Podaj numer zamówienia' }),
  comments: z.string().optional().nullable(),
})

export const userSchema = z.object({
  firstName: z.string({ required_error: 'Podaj imię' }),
  lastName: z.string({ required_error: 'Podaj nazwisko' }),
  username: z.string({ required_error: 'Podaj nazwę użytkownika' }),
  roles: z.array(z.string()).default([]),
  active: z.boolean({ required_error: 'Podaj czy konto jest aktywne' }),
  allowedIPs: z.array(z.string().ip({ message: 'Podaj poprawny adres IP' })).default([]),
})

// { re: /[0-9]/, label: 'Zawiera cyfry' },
//   { re: /[a-z]/, label: 'Zawiera małą literę' },
//   { re: /[A-Z]/, label: 'Zawiera dużą literę' },
//   { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Zawiera znak specjalny' },

const passwordSchema = z.object({
  password: z
    .string({ required_error: 'Podaj hasło' })
    .min(6, { message: 'Hasło musi mieć co najmniej 6 znaków' })
    .regex(/[0-9]/, { message: 'Hasło musi zawierać cyfrę' })
    .regex(/[a-z]/, { message: 'Hasło musi zawierać małą literę' })
    .regex(/[A-Z]/, { message: 'Hasło musi zawierać dużą literę' })
    .regex(/[$&+,:;=?@#|'<>.^*()%!-]/, { message: 'Hasło musi zawierać znak specjalny' }),
  confirmPassword: z.string({ required_error: 'Powtórz hasło' }),
})

const validatePassword = (
  { confirmPassword, password }: { confirmPassword: string; password: string },
  ctx: z.RefinementCtx
) => {
  if (confirmPassword !== password) {
    ctx.addIssue({
      code: 'custom',
      message: 'Hasła nie są takie same',
      path: ['confirmPassword'],
    })
  }
}
export const newUserSchema = userSchema.merge(passwordSchema).superRefine(validatePassword)
export const updatePasswordSchema = passwordSchema.superRefine(validatePassword)

export const orderShortagesSchema = (min: number) =>
  z.object({
    amount: z
      .number({ required_error: 'Podaj ilość zamówionego produktu' })
      .min(min, { message: `Ilość zamówionego produktu musi być większa lub równa ${min}` })
      .positive({ message: 'Ilość musi być większa od zera' }),
    unit: z.nativeEnum(Unit, { required_error: 'Wybierz jednostkę' }),
  })

export const fulfillShortageOrderSchema = z.object({
  fulfilledAt: z.date({ required_error: 'Podaj datę realizacji' }),
  amount: z
    .number({ required_error: 'Podaj ilość zrealizowanego zamówienia' })
    .min(0, { message: 'Ilość zrealizowanego zamówienia nie może być ujemna' }),
  unit: z.nativeEnum(Unit, { required_error: 'Wybierz jednostkę' }),
})

export const updateShortageOrderSchema = (minOrderedAmount: number, status: ShortageStatus) =>
  z.object({
    fulfilledAt:
      status === ShortageStatus.FULFILLED || status === ShortageStatus.UNFULFILLED
        ? z.date({ required_error: 'Podaj datę realizacji' })
        : z.date().optional().nullable(),
    orderedAmount: z.number({ required_error: 'Podaj ilość zamówionego produktu' }).min(minOrderedAmount, {
      message: `Ilość zamówionego produktu musi być większa lub równa ${minOrderedAmount}`,
    }),
    fulfilledAmount:
      status === ShortageStatus.FULFILLED
        ? z
            .number({
              required_error: 'Podaj ilość produktu jaka została zrealizowana',
              invalid_type_error: 'Podaj ilość produktu jaka została zrealizowana',
            })
            .min(0, { message: 'Ilość zrealizowanego zamówienia nie może być ujemna' })
        : z
            .number({ required_error: 'Podaj ilość produktu jaka została zrealizowana' })
            .min(0, { message: 'Ilość zrealizowanego zamówienia nie może być ujemna' })
            .optional()
            .nullable(),
    unit: z.nativeEnum(Unit, { required_error: 'Wybierz jednostkę' }),
    isEmailSent: z.boolean({ required_error: 'Wybierz czy email został wysłany' }),
  })

export const newShortageOrderSchema = z.object({
  productId: z.string({ required_error: 'Wybierz produkt', invalid_type_error: 'Wybierz produkt' }),
  category: z.nativeEnum(Category, {
    required_error: 'Wybierz rodzaj produktu',
    invalid_type_error: 'Wybierz rodzaj produktu',
  }),
  size: z.string({ required_error: 'Podaj wymiar' }).regex(/^(?:\d+(?:x\d+)?)(?:,\d+(?:x\d+)?)*$/, {
    message: 'Podaj poprawny wymiar, np. 250, 140x200 lub 140x200,160x200',
  }),
  feature: z.string().optional().nullable(),
  orderedAmount: z
    .number({ required_error: 'Podaj ilość brakującego produktu' })
    .positive({ message: 'Ilość brakującego produktu musi być większa od zera' }),
  unit: z.nativeEnum(Unit, { required_error: 'Wybierz jednostkę' }),
})

export const getOrderSchema = (isParcelLocker: boolean) =>
  z.object({
    firstName: z.string({ required_error: 'Podaj imię' }),
    lastName: z.string({ required_error: 'Podaj nazwisko' }),
    countryCode: z.preprocess(
      (value) => String(value),
      z
        .string()
        .regex(/^\d{1,3}$/, 'Podaj poprawny kod kraju numeru telefonu')
        .transform(Number)
    ),
    phoneNumber: z.preprocess((value) => String(value), z.string().regex(/^\d{9}$/, 'Podaj poprawny numer telefonu')),
    documentType: z.nativeEnum(DocumentType, { required_error: 'Wybierz dokument sprzedaży' }),
    paymentMethod: z.nativeEnum(PaymentMethod, { required_error: 'Wybierz metodę płatności' }),
    nip: z.preprocess(
      (val) => {
        if (typeof val === 'number') {
          return val.toString()
        }
        return val
      },
      z.union([
        z
          .string()
          .min(10, 'NIP musi mieć 10 cyfr')
          // Dziesiąta cyfra NIP jest cyfrą kontrolną, obliczaną według poniższego algorytmu:
          // Pomnożyć każdą z pierwszych dziewięciu cyfr odpowiednio przez wagi: 6, 5, 7, 2, 3, 4, 5, 6, 7,
          // Zsumować wyniki mnożenia,
          // Obliczyć resztę z dzielenia przez 11 (operacja modulo 11).
          .refine((nip) => {
            if (nip === '') return true
            const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
            const digits = nip.split('').map(Number)
            const sum = digits.slice(0, 9).reduce((acc, digit, index) => acc + digit * weights[index], 0)
            const controlDigit = sum % 11
            return controlDigit === digits[9]
          }, 'Podaj poprawny NIP'),
        z
          .literal('')
          .transform((value) => value || null)
          .nullable(),
      ])
    ),
    discountPercent: z.number().max(100, { message: 'Rabat nie może być większy niż 100%' }).optional().nullable(),
    delivery: isParcelLocker
      ? z.object({
          firstName: z
            .string({ required_error: 'Podaj imię', invalid_type_error: 'Podaj imię' })
            .min(1, { message: 'Podaj imię' }),
          lastName: z
            .string({ required_error: 'Podaj nazwisko', invalid_type_error: 'Podaj nazwisko' })
            .min(1, { message: 'Podaj nazwisko' }),
          name: z.string().nullable().default(null),
          street: z.string().nullable().default(null),
          buildingNumber: z.string().nullable().default(null),
          apartmentNumber: z.string().nullable().default(null),
          city: z.string().nullable().default(null),
          postalCode: z.string().nullable().default(null),
          country: z.string().nullable().default(null),
          deliveryCompanyId: z.string({
            required_error: 'Wybierz firmę kurierską',
            invalid_type_error: 'Wybierz firmę kurierską',
          }),
          shipmentTypeId: z.string({
            required_error: 'Wybierz rodzaj przesyłki',
            invalid_type_error: 'Wybierz rodzaj przesyłki',
          }),
          shippingCost: z
            .number({ required_error: 'Podaj cenę przesyłki', invalid_type_error: 'Podaj cenę przesyłki' })
            .nonnegative({ message: 'Cena przesyłki nie może być ujemna' }),
          parcelLocker: z
            .string({
              required_error: 'Podaj numer paczkomatu',
              invalid_type_error: 'Podaj numer paczkomatu',
            })
            .min(1, { message: 'Podaj numer paczkomatu' }),
          parcelSize: z.string({
            required_error: 'Wybierz rozmiar paczki',
            invalid_type_error: 'Wybierz rozmiar paczki',
          }),
        })
      : z.object({
          firstName: z
            .string({ required_error: 'Podaj imię', invalid_type_error: 'Podaj imię' })
            .min(1, { message: 'Podaj imię' }),
          lastName: z
            .string({ required_error: 'Podaj nazwisko', invalid_type_error: 'Podaj nazwisko' })
            .min(1, { message: 'Podaj nazwisko' }),
          name: z.string().optional().nullable(),
          street: z.string({ required_error: 'Podaj ulicę', invalid_type_error: 'Podaj ulicę' }),
          buildingNumber: z.string({
            required_error: 'Podaj numer budynku',
            invalid_type_error: 'Podaj numer budynku',
          }),
          apartmentNumber: z.string().optional().nullable(),
          city: z.string({ required_error: 'Podaj miejscowość', invalid_type_error: 'Podaj miejscowość' }),
          postalCode: z.string({ required_error: 'Podaj kod pocztowy', invalid_type_error: 'Podaj kod pocztowy' }),
          country: z.string(),
          deliveryCompanyId: z.string({
            required_error: 'Wybierz firmę kurierską',
            invalid_type_error: 'Wybierz firmę kurierską',
          }),
          shipmentTypeId: z.string({
            required_error: 'Wybierz rodzaj przesyłki',
            invalid_type_error: 'Wybierz rodzaj przesyłki',
          }),
          shippingCost: z
            .number({ required_error: 'Podaj cenę przesyłki', invalid_type_error: 'Podaj cenę przesyłki' })
            .nonnegative({ message: 'Cena przesyłki nie może być ujemna' }),
          parcelLocker: z.string().nullable().default(null),
          parcelSize: z.string().nullable().default(null),
        }),
    email: z.string().email({ message: 'Podaj poprawny adres email' }).or(z.literal('')).optional().nullable(),
    comments: z.string().optional().nullable(),
  })

export const orderItemSchema = z.object({
  productSymbol: z.string({ required_error: 'Podaj symbol produktu' }),
  amount: z.number({ required_error: 'Podaj ilość' }).positive({ message: 'Ilość musi być większa od zera' }),
  unit: z.nativeEnum(Unit, { required_error: 'Wybierz jednostkę' }),
  price: z.number({ required_error: 'Podaj cenę' }).nonnegative({ message: 'Cena nie może być ujemna' }),
  size: z.string({ required_error: 'Podaj wymiar' }).regex(/^(?:\d+(?:x\d+)?)(?:,\d+(?:x\d+)?)*$/, {
    message: 'Podaj poprawny wymiar, np. 250, 140x200 lub 140x200,160x200',
  }),
  category: z.nativeEnum(Category, { required_error: 'Wybierz kategorię' }),
  finish: z.string().optional().nullable(),
  comments: z.string().max(65, { message: 'Komentarz nie może przekraczać 65 znaków' }).optional().nullable(),
})

export const shipmentTypeSchema = z.object({
  id: z.string({ required_error: 'Podaj ID rodzaju przesyłki' }),
  name: z
    .string({ required_error: 'Podaj nazwę rodzaju przesyłki' })
    .min(1, { message: 'Podaj nazwę rodzaju przesyłki' }),
  isActive: z.preprocess(
    (value) => (value === '' ? false : value),
    z.boolean({ required_error: 'Wybierz czy jest aktywny' })
  ),
  // shippingCost: zodInputStringPipe(
  //   z.number({ required_error: 'Podaj cenę przesyłki' }).nonnegative({ message: 'Cena przesyłki nie może być ujemna' })
  // ),
  shippingCost: z
    .number({ required_error: 'Podaj cenę przesyłki', invalid_type_error: 'Podaj cenę przesyłki' })
    .nonnegative({ message: 'Cena przesyłki nie może być ujemna' }),
  isParcelLocker: z.preprocess(
    (value) => (value === '' ? false : value),
    z.boolean({ required_error: 'Wybierz czy paczkomat' })
  ),
})

export const deliveryCompanySchema = z.object({
  name: z
    .string({ required_error: 'Podaj nazwę firmy dostawczej' })
    .min(1, { message: 'Podaj nazwę firmy dostawczej' }),
  isActive: z.boolean({ required_error: 'Podaj czy firma dostawcza jest aktywna' }),
  shipmentTypes: z.array(shipmentTypeSchema),
})

export const externalOrderSchema = z.object({
  firstName: z.string({ required_error: 'Podaj imię' }).min(1, { message: 'Podaj imię' }),
  lastName: z.string({ required_error: 'Podaj nazwisko' }).min(1, { message: 'Podaj nazwisko' }),
  email: z.string().email({ message: 'Podaj poprawny adres email' }).or(z.literal('')).optional().nullable(),
  countryCode: z
    .preprocess(
      (value) => {
        if (value === undefined || value === null || value === '') {
          return undefined
        }
        return String(value)
      },
      z
        .string()
        .regex(/^\d{1,3}$/, 'Podaj poprawny kod kraju numeru telefonu')
        .transform(Number)
    )
    .or(z.literal(null))
    .optional()
    .nullable(),
  phoneNumber: z
    .preprocess(
      (value) => {
        if (value === undefined || value === null || value === '') {
          return undefined
        }
        return String(value)
      },
      z.string().regex(/^\d{9}$/, 'Podaj poprawny numer telefonu')
    )
    .or(z.literal(''))
    .optional()
    .nullable(),
})
